import { useEffect, useState } from 'react'
import { isPointerInputBlocked } from '@/lib/hooks/useIsInteractionBlocked'

/**
 * Open dialog surfaces, named by the attributes they actually carry in this app.
 *
 * - Radix Dialog and AlertDialog content mark themselves `data-state="open"` while
 *   open and `"closed"` during the exit animation, then unmount. Radix Popover
 *   content ALSO carries `role="dialog"`, so an open popover counts too. That is
 *   deliberate: telling a popover apart from a dialog would need a second
 *   heuristic, and deferring a suggestion card while a popover is open costs a
 *   few seconds at most. Closed-but-mounted Radix layers say `"closed"` and do
 *   not count.
 * - The hand-rolled full-screen viewers (Gallery, Pdf, LocationMap,
 *   MaintenanceScreen) carry `aria-modal="true"` and render nothing when closed.
 *
 * There is no store of open dialogs in this app to ask instead: dialogs are
 * Radix primitives and local component state, so the DOM is the only place the
 * union of them exists.
 */
export const OPEN_DIALOG_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="dialog"][aria-modal="true"]',
  '[role="alertdialog"][aria-modal="true"]',
].join(', ')

/**
 * Whether any dialog, sheet, popover or other modal layer is open right now.
 *
 * Two independent signals, because each one misses a case the other catches:
 * - the Radix `pointer-events: none` guard on `<body>` (see
 *   useIsInteractionBlocked) also covers modal layers that carry no dialog role
 *   at all — Select (`role="listbox"`) and DropdownMenu (`role="menu"`);
 * - the role selector covers the hand-rolled viewers, and the one place that
 *   deliberately lifts the guard while a Radix dialog is still open
 *   (`NewIssueDialogLocation.tsx` sets `body.style.pointerEvents = 'auto'` to
 *   free the full-screen map).
 *
 * Exported on its own so callers can re-read it at the moment they act, and so
 * it can be pinned in jsdom without mounting React.
 */
export function isAnyDialogOpen(doc: Document = document): boolean {
  return isPointerInputBlocked(doc) || doc.querySelector(OPEN_DIALOG_SELECTOR) !== null
}

/** Whether `node` is, or contains, an element matching OPEN_DIALOG_SELECTOR. */
const holdsOpenDialog = (node: Node): boolean =>
  node.nodeType === Node.ELEMENT_NODE &&
  ((node as Element).matches(OPEN_DIALOG_SELECTOR) ||
    (node as Element).querySelector(OPEN_DIALOG_SELECTOR) !== null)

/**
 * Whether a batch of subtree mutations can have changed the answer of
 * `isAnyDialogOpen()`, so the document is re-queried only when it might have.
 *
 * Complete by construction: the answer depends on the set of elements matching
 * OPEN_DIALOG_SELECTOR (the body style is observed separately), and that set
 * changes only when a matching element is inserted or removed — the inserted or
 * removed node then matches or contains it, and a removed subtree keeps its
 * attributes — or when one of the selector's attributes changes, which the
 * attribute filter delivers and which always counts.
 *
 * Exported so the "skip" half can be pinned: without it, every commit in the app
 * (a virtualised list adds and removes rows on each scroll frame) would run a
 * whole-document query.
 */
export function mayChangeOpenDialogs(records: readonly MutationRecord[]): boolean {
  return records.some(
    record =>
      record.type === 'attributes' ||
      Array.from(record.addedNodes).some(holdsOpenDialog) ||
      Array.from(record.removedNodes).some(holdsOpenDialog),
  )
}

/**
 * Reactive `isAnyDialogOpen()`.
 *
 * Two observers, because the signals live in different places:
 * - `<body>` itself, `style` only: the Radix pointer-events guard is an inline
 *   style on body. Style is NOT watched on the subtree — inline `style` changes on
 *   every frame of a virtualised scroll;
 * - the whole subtree, child list plus the selector's own attributes (`role`,
 *   `data-state`, `aria-modal`): portals mount as direct children of body, but a
 *   modal rendered INLINE mounts deep inside the app root — MaintenanceScreen
 *   does, in GlobalLayout — and a Radix layer flips `data-state` to `"closed"`
 *   for its exit animation while staying mounted. React writes an attribute only
 *   when its value changes, so the attribute half stays quiet; the child-list
 *   half fires on every commit and is filtered by `mayChangeOpenDialogs`.
 *
 * Callers that act on a timer should still re-read `isAnyDialogOpen()` when they
 * act: the observer delivers in a microtask, but the re-render it causes (and
 * with it any effect cleanup that would cancel the timer) is scheduled, so a
 * timer already due can fire in between.
 */
export function useIsAnyDialogOpen(): boolean {
  const [open, setOpen] = useState(() =>
    typeof document === 'undefined' ? false : isAnyDialogOpen(),
  )

  useEffect(() => {
    const read = () => setOpen(isAnyDialogOpen())
    read()

    const bodyStyleObserver = new MutationObserver(read)
    bodyStyleObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    })
    const treeObserver = new MutationObserver(records => {
      if (mayChangeOpenDialogs(records)) read()
    })
    treeObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['role', 'data-state', 'aria-modal'],
    })

    return () => {
      bodyStyleObserver.disconnect()
      treeObserver.disconnect()
    }
  }, [])

  return open
}
